import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import BottomNav from './components/BottomNav';
import Sidebar from './components/Sidebar';
import Landing from './screens/Landing';
import NativeLogin from './screens/NativeLogin';
import SignUp from './screens/SignUp';
import TripList from './screens/TripList';
import NewTripGPS from './screens/NewTripGPS';
import NewTripManual from './screens/NewTripManual';
import TripDetail from './screens/TripDetail';
import Profile from './screens/Profile';
import TermsOfService from './screens/TermsOfService';
import PrivacyPolicy from './screens/PrivacyPolicy';
import Subscription from './screens/Subscription';
import Expenses from './screens/Expenses';
import AddExpense from './screens/AddExpense';
import Reports from './screens/Reports';
import DeleteAccount from './screens/DeleteAccount';
import ErrorBoundary from './components/ErrorBoundary';
import { useDriveDetection } from './hooks/useDriveDetection';
import './App.css';

const isNative = Capacitor.isNativePlatform();
const Router = isNative ? HashRouter : BrowserRouter;

function HomeGuard() {
  const { currentUser } = useAuth();
  if (isNative) {
    return currentUser ? <Navigate to="/dashboard" /> : <NativeLogin />;
  }
  return currentUser ? <Navigate to="/dashboard" /> : <Landing />;
}

function NativePrivateRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/" />;
}

function AppRoutes() {
  const location = useLocation();
  const { currentUser } = useAuth();

  // Start auto-drive detection on native when user is authenticated
  useDriveDetection();

  // Native app layout
  if (isNative) {
    return (
      <div className="app">
        <div className="app-content">
          <Routes>
            <Route path="/" element={<HomeGuard />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/dashboard" element={<NativePrivateRoute><TripList /></NativePrivateRoute>} />
            <Route path="/trip/new/gps" element={<NativePrivateRoute><NewTripGPS /></NativePrivateRoute>} />
            <Route path="/trip/new/manual" element={<NativePrivateRoute><NewTripManual /></NativePrivateRoute>} />
            <Route path="/trip/:tripId" element={<NativePrivateRoute><TripDetail /></NativePrivateRoute>} />
            <Route path="/profile" element={<NativePrivateRoute><Profile /></NativePrivateRoute>} />
            <Route path="/expenses" element={<NativePrivateRoute><Expenses /></NativePrivateRoute>} />
            <Route path="/expenses/add" element={<NativePrivateRoute><AddExpense /></NativePrivateRoute>} />
            <Route path="/reports" element={<NativePrivateRoute><Reports /></NativePrivateRoute>} />
            <Route path="/subscription" element={<NativePrivateRoute><Subscription /></NativePrivateRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
        {currentUser && <BottomNav />}
      </div>
    );
  }

  // Web layout
  const publicPages = ['/login', '/signup', '/terms', '/privacy'];
  const isPublic = publicPages.includes(location.pathname) || location.pathname === '/';
  const showChrome = !isPublic && currentUser;

  const routes = (
    <Routes>
      <Route path="/" element={<HomeGuard />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/dashboard" element={<PrivateRoute><TripList /></PrivateRoute>} />
      <Route path="/drives" element={<PrivateRoute><TripList /></PrivateRoute>} />
      <Route path="/trip/new/gps" element={<PrivateRoute><NewTripGPS /></PrivateRoute>} />
      <Route path="/trip/new/manual" element={<PrivateRoute><NewTripManual /></PrivateRoute>} />
      <Route path="/trip/:tripId" element={<PrivateRoute><TripDetail /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/expenses" element={<PrivateRoute><Expenses /></PrivateRoute>} />
      <Route path="/expenses/add" element={<PrivateRoute><AddExpense /></PrivateRoute>} />
      <Route path="/subscription" element={<PrivateRoute><Subscription /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  if (!showChrome) {
    return <>{routes}</>;
  }

  return (
    <div className="web-layout">
      <Sidebar />
      <main className="web-main">{routes}</main>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
